import type { Evaluation, Profile, Rule, RuleEvaluation, RuleStatus, Work } from './types.ts'
import { deriveCategoryId } from './categorize.ts'

/**
 * Jádro aplikace. Čistá, deterministická, nezávislá na pořadí vstupu.
 * Běží v prohlížeči (živá zpětná vazba) i na serveru (autoritativní kontrola
 * před exportem) — jedna implementace, ne dvě, které se časem rozejdou.
 *
 * Při 108 dílech je přepočet natolik levný, že se volá při každé změně výběru
 * bez memoizace.
 */
export function evaluate(profile: Profile, works: Work[], selectedIds: string[]): Evaluation {
  const index = new Map(works.map((w) => [w.id, w]))

  // deduplikace a zahození neznámých id; pořadí bereme z works, ne z výběru,
  // aby byl výsledek nezávislý na pořadí vstupu
  const selectedSet = new Set(selectedIds)
  const selected = works.filter((w) => selectedSet.has(w.id))

  const categoryOf = new Map<string, string | null>()
  for (const w of selected) categoryOf.set(w.id, deriveCategoryId(w, profile))

  const uncategorizedWorkIds = selected.filter((w) => categoryOf.get(w.id) === null).map((w) => w.id)

  const rules = profile.rules.map((rule) => evaluateRule(rule, profile, selected, categoryOf, index))

  const unmet = rules.filter((r) => r.status === 'missing')
  const unmetCategoryIds = profile.rules
    .filter((r) => r.type === 'minInCategory' && unmet.some((u) => u.ruleId === r.id))
    .map((r) => (r as Extract<Rule, { type: 'minInCategory' }>).categoryId)
  const unmetFormIds = profile.rules
    .filter((r) => r.type === 'minByForm' && unmet.some((u) => u.ruleId === r.id))
    .map((r) => (r as Extract<Rule, { type: 'minByForm' }>).formId)

  return {
    valid: rules.every((r) => r.status === 'ok'),
    rules,
    unmetCategoryIds,
    unmetFormIds,
    uncategorizedWorkIds,
  }
}

function evaluateRule(
  rule: Rule,
  profile: Profile,
  selected: Work[],
  categoryOf: Map<string, string | null>,
  index: Map<string, Work>,
): RuleEvaluation {
  switch (rule.type) {
    case 'totalCount': {
      const current = selected.length
      // u celkového počtu nejde říct, která díla jsou „ta přebývající“ —
      // offendingWorkIds proto zůstává prázdné a UI u něj nenabízí rozbalení
      return build(rule.id, 'Celkový počet děl', current, rule.min, rule.max, [], {
        missing: (n) => `Chybí ${plural(n, 'dílo', 'díla', 'děl')}`,
        excess: (n) => `${cap(plural(n, 'dílo', 'díla', 'děl'))} navíc — něco odeber`,
      })
    }

    case 'minInCategory': {
      const category = profile.categories.find((c) => c.id === rule.categoryId)
      const hits = selected.filter((w) => categoryOf.get(w.id) === rule.categoryId)
      const label = category?.label ?? `Neznámá kategorie „${rule.categoryId}“`
      return build(rule.id, label, hits.length, rule.min, rule.max, hits.map((w) => w.id), {
        missing: (n) => `Chybí ${plural(n, 'dílo', 'díla', 'děl')}`,
        excess: (n) => `${cap(plural(n, 'dílo', 'díla', 'děl'))} navíc`,
      })
    }

    case 'minByForm': {
      const form = profile.forms.find((f) => f.id === rule.formId)
      const hits = selected.filter((w) => w.formId === rule.formId)
      const label = form?.label ?? `Neznámá forma „${rule.formId}“`
      return build(rule.id, label, hits.length, rule.min, undefined, [], {
        missing: (n) => `Chybí ${plural(n, 'dílo', 'díla', 'děl')}`,
        excess: () => '',
      })
    }

    case 'maxPerAuthor': {
      // anonymní díla se nesčítají — dvě díla bez autora nejsou dvě díla téhož autora
      const byAuthor = new Map<string, Work[]>()
      for (const w of selected) {
        if (!w.author) continue
        const bucket = byAuthor.get(w.author)
        if (bucket) bucket.push(w)
        else byAuthor.set(w.author, [w])
      }
      const offenders = [...byAuthor.entries()].filter(([, ws]) => ws.length > rule.max)
      const current = [...byAuthor.values()].reduce((m, ws) => Math.max(m, ws.length), 0)
      const offendingWorkIds = offenders.flatMap(([, ws]) => ws.map((w) => w.id)).sort()
      const names = offenders.map(([a, ws]) => `${a} má ${plural(ws.length, 'dílo', 'díla', 'děl')}`)

      return {
        ruleId: rule.id,
        label: 'Nejvíc děl od jednoho autora',
        status: offenders.length > 0 ? 'excess' : 'ok',
        current,
        required: rule.max,
        hint: offenders.length > 0 ? `${names.join(', ')} — nadbytečná odeber` : '',
        offendingWorkIds,
      }
    }

    case 'requiredWork': {
      const work = index.get(rule.workId)
      const present = selected.some((w) => w.id === rule.workId)
      return {
        ruleId: rule.id,
        label: `Povinné dílo: ${work?.title ?? rule.workId}`,
        status: present ? 'ok' : 'missing',
        current: present ? 1 : 0,
        required: 1,
        hint: present ? '' : 'Toto dílo musí být v seznamu',
        offendingWorkIds: [],
      }
    }
  }
}

function build(
  ruleId: string,
  label: string,
  current: number,
  min: number,
  max: number | undefined,
  candidates: string[],
  hints: { missing: (n: number) => string; excess: (n: number) => string },
): RuleEvaluation {
  let status: RuleStatus = 'ok'
  let hint = ''
  let offendingWorkIds: string[] = []

  if (current < min) {
    status = 'missing'
    hint = hints.missing(min - current)
  } else if (max !== undefined && current > max) {
    status = 'excess'
    hint = hints.excess(current - max)
    offendingWorkIds = candidates
  }

  return { ruleId, label, status, current, required: min, hint, offendingWorkIds }
}

/** České skloňování po číslovce: 1 dílo, 2–4 díla, 5+ děl. */
function plural(n: number, one: string, few: string, many: string): string {
  if (n === 1) return `${n} ${one}`
  if (n >= 2 && n <= 4) return `${n} ${few}`
  return `${n} ${many}`
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)
